import { v4 as uuidv4 } from "uuid";
import { Buffer } from "buffer";
import { registerPayload, safetyRequestFor } from "@/services/safety";
import { apiHeaders } from "@/services/authHeaders";
import { FallbackToLegacy, uploadViaQueue } from "@/services/presignedUpload";

const baseUrl = process.env.VUE_APP_API_URL;

// PUT a file to a presigned URL with upload progress. Resolves {status} for
// any HTTP answer; rejects only on a network error (nothing reached Spaces,
// or CORS blocked it), which uploadViaQueue tells apart from mid-body drops.
function putWithProgress(url, headers, file, onProgress) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    for (const [name, value] of Object.entries(headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.addEventListener("progress", (e) => {
      // Safari can fire progress with total 0; don't poison the meter.
      if (!e.lengthComputable || !e.total) return;
      onProgress((e.loaded / e.total) * 100);
    });
    request.addEventListener("load", () => resolve({ status: request.status }));
    request.addEventListener("error", () =>
      reject(new Error("Network error during upload")),
    );
    request.addEventListener("abort", () =>
      reject(new Error("Upload aborted")),
    );
    request.send(file);
  });
}
console.log(baseUrl);

export default {
  methods: {
    // Signed out, no Authorization header at all (never "Bearer null"):
    // see services/authHeaders.
    getFetchOptions() {
      return {
        credentials: "include",
        headers: apiHeaders(window.localStorage.getItem("JWT")),
      };
    },
    validateToken() {
      // Asks the API whether the stored JWT is still valid. Resolves to a
      // boolean and never rejects, so the router guard can rely on it.
      return fetch(`${baseUrl}/api/auth/validate`, this.getFetchOptions())
        .then((response) => response.ok)
        .catch(() => false);
    },
    fetchSessionMediaTokens(sessionName) {
      // Short-lived signed tokens for a session's loops (stream + download) and
      // its library/als artifacts. Returns the results object, or null if the
      // call fails so callers can degrade gracefully.
      return fetch(
        `${baseUrl}/api/media/session-urls?session_name=${encodeURIComponent(
          sessionName,
        )}`,
        this.getFetchOptions(),
      )
        .then((response) => {
          if (!response.ok) throw response.status;
          return response.json();
        })
        .then((body) => (body && body.data && body.data.results) || null);
    },
    fetchUserSessions(userName) {
      return new Promise((resolve, reject) => {
        fetch(
          `${baseUrl}/api/sessions?username=${userName}`,
          this.getFetchOptions(),
        )
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    fetchUser(userName) {
      return new Promise((resolve, reject) => {
        fetch(
          `${baseUrl}/api/user?username=${userName}`,
          this.getFetchOptions(),
        )
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    
    fetchUserLoops(userName) {
      return new Promise((resolve, reject) => {
        const url = userName 
          ? `${baseUrl}/api/get_loops?user=${userName}`
          : `${baseUrl}/api/get_loops`;
        fetch(url, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchFavoriteLoops(userName) {
      return new Promise((resolve, reject) => {
        fetch(
          `${baseUrl}/api/favorite_loops?username=${encodeURIComponent(userName)}`,
          this.getFetchOptions(),
        )
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchSession(sessionName) {
      return new Promise((resolve, reject) => {
        fetch(
          `${baseUrl}/api/session?session_name=${encodeURIComponent(sessionName)}`,
          this.getFetchOptions(),
        )
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchPublicSession(sessionName) {
      // Unauthenticated read of a PUBLIC session — powers logged-out playback
      // and shareable links. No Authorization header (the endpoint ignores it),
      // and a private/missing session comes back as 404.
      return new Promise((resolve, reject) => {
        fetch(
          `${baseUrl}/api/session/public?session_name=${encodeURIComponent(sessionName)}`,
          { headers: { "Content-Type": "application/json" } },
        )
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchUserProfile(userName, vue) {
      const token = window.localStorage.getItem("JWT");
      if (token) {
        return new Promise((resolve, reject) => {
          const fetchOptions = {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `JWT ${token}`,
            },
          };
          fetch(
            `${baseUrl}/api/user${userName ? `?username=${userName}` : ""}`,
            fetchOptions,
          )
            .then((response) => {
              if (response.ok) return response.json();
              else reject(response.json().message);
            })
            .then(({ data: { results } }) => {
              console.log({ results });
              vue.$store.commit("setSelectedProfile", results);
              resolve(results);
            });
        });
      }
    },

    fetchFavoriteSessions() {
      this.loading = true;
      const { userName } = this.$route.params;
      return new Promise((resolve, reject) => {
        fetch(
          `${baseUrl}/api/favorite_sessions?username=${userName}`,
          this.getFetchOptions(),
        )
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.json().message);
          })
          .then(({ data: { results } }) => {
            this.sessions = results;
            this.loading = false;
            resolve;
            console.log("Got Favorite sessions", results);
          });
      });
    },

    // Randomized discovery feed of public sessions (GET /feed) — the same
    // source the native app's Feed tab uses. Shuffled server-side, skips empty
    // sessions and your own. `ignore` is the names already on screen, so
    // scrolling keeps surfacing fresh sessions until the public set runs out
    // (the server returns fewer/none rather than repeating).
    fetchFeed({ take = 24, ignore = [] } = {}) {
      const params = new URLSearchParams({ take: String(take) });
      if (ignore.length) params.set("ignore", ignore.join(","));
      return fetch(`${baseUrl}/api/feed?${params}`, this.getFetchOptions()).then(
        (response) => {
          if (!response.ok) throw response.status;
          return response.json();
        },
      );
    },

    // query: an optional "?view=list&limit=N" page; without it, every public
    // session (the old clients' shape).
    fetchPublicSessionHeaders(query = "") {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/publicSessionHeaders${query}`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    fetchRecentSessionHeaders() {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/recentSessionHeaders`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    followUser(friendUsername) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/add_friend`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({ friend_username: friendUsername }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    unfollowUser(friendUsername) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/remove_friend`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({ friend_username: friendUsername }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    // ---- Safety: block / report / delete account --------------------------
    // These reject with { status, message } (message is the server's, if any).

    safetyRequest({ path, method, body: payload }) {
      return fetch(`${baseUrl}/api${path}`, {
        ...this.getFetchOptions(),
        method,
        ...(payload !== undefined ? { body: JSON.stringify(payload) } : {}),
      }).then(async (response) => {
        const body = await response.json().catch(() => null);
        if (!response.ok) {
          throw {
            status: response.status,
            message: body?.message || null,
            results: body?.data?.results ?? null,
          };
        }
        return body && body.data ? body.data.results : null;
      });
    },
    blockUser(username) {
      // Ends any friendship both ways and declines pending invitations
      // between the two of you. Idempotent.
      return this.safetyRequest(safetyRequestFor("block", username));
    },
    unblockUser(username) {
      return this.safetyRequest(safetyRequestFor("unblock", username));
    },
    getBlockedUsers() {
      // Resolves to { blocked: [usernames] } (people I blocked).
      return this.safetyRequest(safetyRequestFor("blocks"));
    },
    reportContent(payload) {
      // payload: buildReportPayload() output. Resolves to { id }.
      return this.safetyRequest(safetyRequestFor("report", payload));
    },
    deleteAccount(password) {
      // Permanently deletes the signed-in account. 403 = wrong password;
      // 503 = not finished, safe to call again. Resolves to the summary.
      return this.safetyRequest(safetyRequestFor("deleteAccount", password));
    },

    fetchFriendsSessionHeaders() {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/friends_sessions`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    fetchTopSessionHeaders() {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/topSessionHeaders`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    createSession(sessionName, isPrivate = false, isLooping = false) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/session`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({
            session_name: sessionName,
            private_session: isPrivate,
            is_looping: isLooping,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    // Set a session's display name (alt_name). The immutable `name` key is
    // never touched, so favorites, friends' sessions and share links keep
    // resolving — this only changes the label. Creator-only on the server.
    // Passing an empty string clears back to the key name. Returns the updated
    // session doc; the server also broadcasts state_update over the socket.
    renameSession(sessionName, altName) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/session/rename`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({
            session_name: sessionName,
            alt_name: altName,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          // Unwrap to the session DOC (same as toggleSessionPublic): callers
          // commit this straight into selectedSession, and committing the raw
          // envelope blanks the whole session view.
          .then((body) => resolve(body && body.data && body.data.results))
          .catch(reject);
      });
    },

    // Import an Ableton project zip / .als / .alp: the server unpacks it,
    // creates a session (looping when it's one of our exported sets), ingests
    // each sample as a loop, and returns { session_name, imported, total }.
    // Supports optional onProgress callback for upload progress and processing states.
    importAbletonArchive(file, onProgress) {
      return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open("POST", `${baseUrl}/upload/ableton`);
        const token = window.localStorage.getItem("JWT");
        if (token) {
          request.setRequestHeader("Authorization", `Bearer ${token}`);
        }

        if (request.upload && typeof onProgress === "function") {
          request.upload.addEventListener("progress", (e) => {
            if (!e.lengthComputable || !e.total) return;
            const percent = (e.loaded / e.total) * 100;
            onProgress({
              loaded: e.loaded,
              total: e.total,
              percent,
              status: percent >= 100 ? "processing" : "uploading",
            });
          });
        }

        request.addEventListener("load", () => {
          if (request.status >= 200 && request.status < 300) {
            try {
              const data = JSON.parse(request.response);
              if (typeof onProgress === "function") {
                onProgress({
                  loaded: file.size,
                  total: file.size,
                  percent: 100,
                  status: "complete",
                });
              }
              resolve(data);
            } catch (_) {
              resolve(request.response);
            }
            return;
          }
          let message = `Import failed (HTTP ${request.status})`;
          try {
            const body = JSON.parse(request.response);
            if (typeof body === "string") message = body;
            else if (body && body.message) message = body.message;
          } catch (_) {
            /* not JSON: keep the HTTP status message */
          }
          reject(new Error(message));
        });

        request.addEventListener("error", () => {
          reject(new Error("Network error during archive upload"));
        });

        request.addEventListener("abort", () => {
          reject(new Error("Upload aborted"));
        });

        request.send(file);
      });
    },

    deleteSession(sessionName) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/deleteSession`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({
            session_name: sessionName,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    deleteSlot(sessionName, slotId) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/delete_slot`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({
            session_name: sessionName,
            slot_number: slotId,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    logInUser(username, password) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/auth/login`, {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({
            username,
            password,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    registerUser(username, email, password, referredBy = null, acceptTerms = false) {
      // referredBy: optional attribution (?via= share links / invite inviter);
      // the server validates it names a real other user and drops it otherwise.
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/register_user`, {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          // accept_terms only from a form that shows the 13+ / Terms
          // statement (TermsConsent); the server stores when and which version.
          body: JSON.stringify(
            registerPayload(username, email, password, referredBy, acceptTerms),
          ),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    requestPasswordReset(email) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/auth/reset_password_request`, {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({
            email,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    resetPassword(token, newPassword) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/auth/reset_password`, {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({
            token,
            new_password: newPassword,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    logOutUser() {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/auth/logout`, {
          ...this.getFetchOptions(),
          method: "POST",
        })
          .then((response) => {
            if (response.ok) return resolve();
            else reject("Error logging out user");
          })
          .catch(reject);
      });
    },
    joinSession(username, session_name) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/join`, {
          method: "POST",
          headers: {
            ...this.getFetchOptions().headers,
          },
          body: JSON.stringify({
            username: username,
            session_name: session_name,
          }),
        })
          .then((response) => {
            if (response.ok) return resolve();
            else reject("Error joining session.");
          })
          .catch(reject);
      });
    },
    uploadAudio(file, username, sessionName) {
      return this.uploadAudioWithProgress(file, username, sessionName, uuidv4(), null);
    },
    async updateLoopName(hash, name) {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/loop/name`,
          {
            method: "POST",
            headers: this.getFetchOptions().headers,
            body: JSON.stringify({
              loop_hash: hash,
              name: name,
              // Lets the server update the live session doc + broadcast a
              // state_update so the rename sticks (and shows for collaborators).
              session_name:
                (this.$store &&
                  this.$store.state &&
                  this.$store.state.selectedSession &&
                  this.$store.state.selectedSession.name) ||
                (this.$route && this.$route.params && this.$route.params.sessionName) ||
                undefined,
            }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to update loop name");
        }

        this.isEditing = false;
        this.$emit("nameUpdated", {
          hash: hash,
          name: name,
        });
        return true;
      } catch (error) {
        console.error("Error updating loop name:", error);
        this.$emit("error", "Failed to update loop name");
        // Resolves false so the caller can put the old name back and say so.
        return false;
      }
    },

    // ---- loop stars / favorites (durable, per-user) ----
    starLoop(hash) {
      return fetch(`${baseUrl}/api/loops/${encodeURIComponent(hash)}/star`, {
        ...this.getFetchOptions(),
        method: "POST",
      }).then((r) => {
        if (!r.ok) throw r.status;
        return r.json();
      });
    },
    unstarLoop(hash) {
      return fetch(`${baseUrl}/api/loops/${encodeURIComponent(hash)}/star`, {
        ...this.getFetchOptions(),
        method: "DELETE",
      }).then((r) => {
        if (!r.ok) throw r.status;
        return r.json();
      });
    },
    fetchStarredLoops() {
      return fetch(`${baseUrl}/api/starred_loops`, this.getFetchOptions()).then(
        (r) => {
          if (!r.ok) throw r.status;
          return r.json();
        },
      );
    },

    // Presigned upload through the media job queue (cloudloop-flask
    // UPLOADS.md), falling back to the streaming POST /upload when the queue
    // is off or unreachable. Resolves when the loop is stored and in the
    // session; rejects with the server's reason otherwise.
    async uploadAudioWithProgress(file, username, sessionName, uuid, vue) {
      const setProgress = (progress) => {
        if (vue) vue.$store.commit("updateFileProgress", { uuid, progress });
      };
      try {
        const job = await uploadViaQueue({
          file,
          sessionName,
          loopHash: uuid,
          loopType: "Loop",
          api: this.uploadQueueApi(),
          putFile: putWithProgress,
          onProgress: setProgress,
        });
        return job.result;
      } catch (error) {
        if (!(error instanceof FallbackToLegacy)) throw error;
        console.info(`${error.message}; using the streaming upload`);
        return this.legacyUploadWithProgress(
          file,
          username,
          sessionName,
          uuid,
          vue,
        );
      }
    },
    uploadQueueApi() {
      const call = async (method, path, body) => {
        const response = await fetch(`${baseUrl}${path}`, {
          ...this.getFetchOptions(),
          method,
          body: body === undefined ? undefined : JSON.stringify(body),
        });
        let json = null;
        try {
          json = await response.json();
        } catch (_) {
          // empty or non-JSON body
        }
        return { status: response.status, json };
      };
      return {
        presign: (body) => call("POST", "/upload/presign", body),
        complete: (jobId) =>
          call("POST", `/upload/jobs/${encodeURIComponent(jobId)}/complete`),
        job: (jobId) =>
          call("GET", `/upload/jobs/${encodeURIComponent(jobId)}`),
      };
    },
    legacyUploadWithProgress(file, username, sessionName, uuid, vue) {
      const sessionNameEncoded = Buffer.from(
        new TextEncoder().encode(sessionName),
      ).toString("hex");

      return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open("POST", `${baseUrl}/upload`);
        request.setRequestHeader("CloudLoop-Loop-Creator", username);
        request.setRequestHeader("CloudLoop-Loop-Session", sessionNameEncoded);
        request.setRequestHeader("CloudLoop-Loop-Hash", uuid);
        request.setRequestHeader("CloudLoop-Loop-Type", "Loop");
        const headers = this.getFetchOptions().headers;
        for (const prop in headers) {
          request.setRequestHeader(prop, headers[prop]);
        }

        request.upload.addEventListener("progress", (e) => {
          // Safari can fire progress events with total 0 / not length
          // computable; dividing would poison the meter with NaN.
          if (!e.lengthComputable || !e.total || !vue) return;
          vue.$store.commit("updateFileProgress", {
            uuid,
            progress: (e.loaded / e.total) * 100,
          });
        });

        request.addEventListener("load", () => {
          if (request.status >= 200 && request.status < 300) {
            if (vue) vue.$store.commit("updateFileProgress", { uuid, progress: 100 });
            resolve(request.response);
            return;
          }
          // Surface the server-side message (e.g. quota_exceeded) instead of
          // letting a 4xx slip through as a successful resolve.
          let message = `Upload failed (HTTP ${request.status})`;
          try {
            const body = JSON.parse(request.response);
            if (body && body.message) message = body.message;
          } catch (_) {
            // non-JSON body; keep the generic message
          }
          reject(new Error(message));
        });

        request.addEventListener("error", () => {
          reject(new Error("Network error during upload"));
        });

        request.send(file);
      });
    },
    reserveSlot(sessionName) {
      const newId = uuidv4();
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/reserve_slot`, {
          method: "POST",
          headers: {
            ...this.getFetchOptions().headers,
          },
          body: JSON.stringify({
            new_id: newId,
            session_name: sessionName,
          }),
        })
          .then((response) => {
            if (response.ok) return resolve();
            else reject("Error reserving slot.");
          })
          .catch(reject);
      });
    },
    updateSlot(sessionName, wavLink, slotNo, hash) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/update_slot`, {
          method: "POST",
          headers: {
            ...this.getFetchOptions().headers,
          },
          body: JSON.stringify({
            session_name: sessionName,
            wav_link: wavLink,
            slot_number: slotNo,
            hash: hash,
          }),
        })
          .then((response) => {
            if (response.ok) return resolve();
            else reject("Error updating slot.");
          })
          .catch(reject);
      });
    },
    setSessionPicture(pictureLink, session_name) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/session_picture`, {
          method: "POST",
          headers: {
            ...this.getFetchOptions().headers,
          },
          body: JSON.stringify({
            session_name: session_name,
            picture_link: pictureLink,
          }),
        })
          .then((response) => {
            if (response.ok) return resolve();
            else reject("Error setting session picture.");
          })
          .catch(reject);
      });
    },
    uploadImage(file) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append("file", file);
        const headers = { ...this.getFetchOptions().headers };
        // Let the browser set the multipart Content-Type with its boundary.
        delete headers["Content-Type"];
        delete headers["content-type"];
        fetch(`${baseUrl}/api/upload_image`, {
          method: "POST",
          headers,
          body: formData,
        })
          .then(async (response) => {
            if (!response.ok) {
              const body = await response.json().catch(() => ({}));
              return reject(body.message || `Image upload failed (${response.status})`);
            }
            const body = await response.json();
            const url = body?.data?.results?.picture_link;
            if (!url) return reject("Image upload response missing picture_link");
            resolve(url);
          })
          .catch(reject);
      });
    },
    setBlurb(blurb, session_name) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/blurb`, {
          method: "POST",
          headers: {
            ...this.getFetchOptions().headers,
          },
          body: JSON.stringify({
            session_name: session_name,
            blurb: blurb,
          }),
        })
          .then((response) => {
            // Reject with the status (like renameSession) so the caller can
            // tell "you're not the owner" from "the write was refused".
            if (response.ok) return resolve();
            else reject(response.status);
          })
          .catch(reject);
      });
    },
    addFavorite(session_name) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/add_favorite`, {
          method: "POST",
          headers: {
            ...this.getFetchOptions().headers,
          },
          body: JSON.stringify({
            session_name: session_name,
          }),
        })
          .then((response) => {
            if (response.ok) return resolve();
            else reject("Error adding favorite");
          })
          .catch(reject);
      });
    },
    removeFavorite(session_name) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/remove_favorite`, {
          method: "POST",
          headers: {
            ...this.getFetchOptions().headers,
          },
          body: JSON.stringify({
            session_name: session_name,
          }),
        })
          .then((response) => {
            if (response.ok) return resolve();
            else reject("Error removing favorite.");
          })
          .catch(reject);
      });
    },
    updateUserBio(bioText) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/bio`, {
          method: "POST",
          headers: {
            ...this.getFetchOptions().headers,
          },
          body: JSON.stringify({
            bio_text: bioText,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject("Error updating bio.");
          })
          .then(resolve)
          .catch(reject);
      });
    },
    omnisearch(query) {
      console.log("Search query:", query);
      fetch(`${baseUrl}/api/omnisearch?query=${query}`, this.getFetchOptions())
        .then((response) => {
          if (response.ok) {
            response.json().then((data) => {
              console.log("Search response data:", data);
              this.userResults = data.data.results.users || [];
              this.sessionResults = data.data.results.sessions || [];
              console.log(
                `Got ${this.userResults.length} users and ${this.sessionResults.length} sessions for query ${query}`,
              );
              console.log("User results:", this.userResults);
              console.log("Session results:", this.sessionResults);
            });
          } else {
            console.log("Search response not ok:", response.status);
            response
              .json()
              .then((errorData) => {
                console.log("Error data:", errorData);
              })
              .catch((err) => {
                console.log("Could not parse error response:", err);
              });
          }
        })
        .catch((error) => {
          console.error("Search fetch error:", error);
        });
    },

    fetchStorageUsage(userName) {
      return new Promise((resolve, reject) => {
        const url = userName
          ? `${baseUrl}/api/storage/usage?username=${userName}`
          : `${baseUrl}/api/storage/usage`;
        fetch(url, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchStorageBreakdown(userName) {
      return new Promise((resolve, reject) => {
        const url = userName
          ? `${baseUrl}/api/storage/breakdown?username=${userName}`
          : `${baseUrl}/api/storage/breakdown`;
        fetch(url, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    // Purchase flow methods
    checkLoopAccess(loopHash, sessionName = null) {
      return new Promise((resolve, reject) => {
        const url = sessionName
          ? `${baseUrl}/api/loops/${loopHash}/access?session=${sessionName}`
          : `${baseUrl}/api/loops/${loopHash}/access`;
        fetch(url, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    // The loop's share link (decision 25, "Copy loop link"):
    // GET /api/loops/<hash>/link -> { url: "https://<host>/l/<slug>", slug }.
    // JWT optional; a loop the caller may not see is a 404. Resolves to the
    // results; rejects with the HTTP status.
    getLoopLink(loopHash) {
      // Signed out, getFetchOptions sends no Authorization header.
      return fetch(
        `${baseUrl}/api/loops/${encodeURIComponent(loopHash)}/link`,
        this.getFetchOptions(),
      ).then(async (response) => {
        if (!response.ok) throw response.status;
        const body = await response.json().catch(() => null);
        return (body && body.data && body.data.results) || null;
      });
    },

    getLoopLicense(loopHash) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/loops/${loopHash}/license`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    createPurchase(loopHash, amountUsd, provider = 'paypal') {
      return this.startPurchase(loopHash, amountUsd, provider).catch((err) => {
        throw err && typeof err.status === 'number' ? err.status : err;
      });
    },

    // POST /api/purchases. Rejects with { status, error, message, min }: the
    // server answers 409 for several reasons (owned, an earlier checkout
    // still open or settling, not for sale), told apart by the body. A
    // network failure rejects with the fetch error.
    startPurchase(loopHash, amountUsd, provider = 'paypal') {
      return fetch(`${baseUrl}/api/purchases`, {
        ...this.getFetchOptions(),
        method: 'POST',
        body: JSON.stringify({
          loop_hash: loopHash,
          amount_usd: amountUsd,
          provider: provider,
        }),
      }).then(async (response) => {
        if (response.ok) return response.json();
        const body = await response.json().catch(() => null);
        const results = (body && body.data && body.data.results) || {};
        throw {
          status: response.status,
          error: results.error || null,
          message: (body && body.message) || '',
          min: results.min_usd != null ? results.min_usd : null,
        };
      });
    },

    // Remove a loop from a session's library. Allowed for the loop's uploader
    // or the session owner (server-enforced); the room gets a state_update.
    deleteLoopInSession(sessionName, loopHash) {
      return fetch(`${baseUrl}/api/delete_loop_in_session`, {
        ...this.getFetchOptions(),
        method: 'POST',
        body: JSON.stringify({ session_name: sessionName, loop_id: loopHash }),
      }).then((response) => {
        if (!response.ok) throw response.status;
      });
    },

    // License-to-fork. Resolves with { status, body } for every HTTP answer
    // (402 carries the buy-list the caller renders); rejects only on network
    // failure. Reusing requestId makes a retry idempotent server-side.
    forkSession(sessionName, { newName = null, requestId = null } = {}) {
      return fetch(`${baseUrl}/api/session/fork`, {
        ...this.getFetchOptions(),
        method: 'POST',
        body: JSON.stringify({
          session_name: sessionName,
          new_name: newName || undefined,
          fork_request_id: requestId || undefined,
        }),
      }).then(async (response) => ({
        status: response.status,
        body: await response.json().catch(() => null),
      }));
    },

    completePurchase(sessionId) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/purchases/complete`, {
          ...this.getFetchOptions(),
          method: 'POST',
          body: JSON.stringify({
            session_id: sessionId,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchPurchases(status = null) {
      return new Promise((resolve, reject) => {
        const url = status
          ? `${baseUrl}/api/purchases?status=${status}`
          : `${baseUrl}/api/purchases`;
        fetch(url, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    // { detail: true }: the sales history (GET /sales?detail=1): sound
    // sales and pack sales, a pack's per-sound rows folded into it.
    fetchSales(status = null, { detail = false } = {}) {
      return new Promise((resolve, reject) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (detail) params.set("detail", "1");
        const qs = params.toString();
        const url = `${baseUrl}/api/sales${qs ? `?${qs}` : ""}`;
        fetch(url, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    // ------------------------------- packs -------------------------------- //
    // Manufacture a pack from a session's library (or an explicit loop_hashes
    // list). Free (CC) loops ride along as $0 downloads; paid ones must be the
    // caller's own (v0 single-payee). Pass publish:true to list it immediately.
    createPack(payload) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/packs`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify(payload),
        })
          .then((response) =>
            response.json().then((body) =>
              response.ok ? resolve(body) : reject(body || response.status)
            )
          )
          .catch(reject);
      });
    },

    publishPack(slug) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/packs/${encodeURIComponent(slug)}/publish`, {
          ...this.getFetchOptions(),
          method: "POST",
        })
          .then((response) =>
            response.json().then((body) =>
              response.ok ? resolve(body) : reject(body || response.status)
            )
          )
          .catch(reject);
      });
    },

    fetchPacks(params = {}) {
      const qs = new URLSearchParams(params).toString();
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/packs${qs ? `?${qs}` : ""}`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchPack(slug) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/packs/${encodeURIComponent(slug)}`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    purchasePack(slug, amountUsd) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/packs/${encodeURIComponent(slug)}/purchase`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({ amount_usd: amountUsd }),
        })
          .then((response) =>
            response.json().then((body) =>
              response.ok ? resolve(body) : reject(body || response.status)
            )
          )
          .catch(reject);
      });
    },

    completePackPurchase(orderId) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/packs/purchase/complete`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({ order_id: orderId }),
        })
          .then((response) =>
            response.json().then((body) =>
              response.ok ? resolve(body) : reject(body || response.status)
            )
          )
          .catch(reject);
      });
    },

    // Whole-pack .als bundle. Needs the JWT header (so not a plain link):
    // fetch with auth, then trigger a blob download. Resolves on success,
    // rejects with a message (e.g. 402 → buy first).
    downloadPackBundle(slug) {
      return fetch(
        `${baseUrl}/api/packs/${encodeURIComponent(slug)}/download`,
        this.getFetchOptions()
      ).then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw (
            body.message ||
            (response.status === 402
              ? "Buy the pack to download the project."
              : "Download failed.")
          );
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slug}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
    },

    // Everything the logged-in user has bought — completed pack purchases and
    // every individually licensed loop (à la carte or fanned out from a pack),
    // each loop carrying fresh media tokens — for the My Audio Purchased tab.
    // Fork ancestry + counts for a session (public info; jwt optional
    // server-side). Resolves to the results object or null — callers render
    // nothing on null rather than failing the page.
    fetchSessionLineage(sessionName) {
      return fetch(
        `${baseUrl}/api/session/${encodeURIComponent(sessionName)}/lineage`,
        this.getFetchOptions(),
      )
        .then((response) => (response.ok ? response.json() : null))
        .then((body) => (body && body.data && body.data.results) || null)
        .catch(() => null);
    },

    // GET /loop?id=<hash>: one sound's loop and metadata fields (its name,
    // creator, licence). Resolves to the record, or null when the server
    // can't find it or can't be reached.
    fetchLoopRecord(loopHash) {
      return fetch(
        `${baseUrl}/api/loop?id=${encodeURIComponent(loopHash)}`,
        this.getFetchOptions(),
      )
        .then((response) => (response.ok ? response.json() : null))
        .then((body) => (body && body.data && body.data.results) || null)
        .catch(() => null);
    },

    // { detail: true } adds GET /sounds-shaped rows (with the creators'
    // display names) for every licensed sound.
    fetchPurchasedLibrary({ detail = false } = {}) {
      return new Promise((resolve, reject) => {
        const url = `${baseUrl}/api/purchases/library${detail ? "?detail=1" : ""}`;
        fetch(url, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    // Free (CC) pack assets download without a purchase; the server gates the
    // endpoint to $0 assets in a published pack, so a plain window.open works.
    downloadFreePackAsset(slug, loopHash) {
      window.open(
        `${baseUrl}/download/pack-asset/${encodeURIComponent(
          slug
        )}/${encodeURIComponent(loopHash)}`,
        "_blank"
      );
    },

    fetchPaymentSettings() {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/payment-settings`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    updatePaymentSettings(settings) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/payment-settings`, {
          ...this.getFetchOptions(),
          method: "PUT",
          body: JSON.stringify(settings),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    createStripeConnect() {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/payment-settings/connect`, {
          ...this.getFetchOptions(),
          method: 'POST',
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    updateLoopPricing(loopHash, licenseType, minPrice, suggestedPrice) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/loops/${loopHash}/pricing`, {
          ...this.getFetchOptions(),
          method: 'PUT',
          body: JSON.stringify({
            license_type: licenseType,
            min_price_usd: minPrice,
            suggested_price_usd: suggestedPrice,
          }),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },
    // ==================== INVITATIONS ====================

    fetchInbox() {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/invitations/inbox`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchInboxCount() {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/invitations/inbox/count`, this.getFetchOptions())
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    sendInvitation(sessionName, inviteeUsername, inviteeEmail, message) {
      const body = { session_name: sessionName };
      if (inviteeUsername) body.invitee_username = inviteeUsername;
      if (inviteeEmail) body.invitee_email = inviteeEmail;
      if (message) body.message = message;

      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/invitations`, {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify(body),
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    toggleSessionPublic(sessionName) {
      // Flip a session's public/private flag. Owner-only, enforced server-side
      // (403 otherwise). Resolves to the updated session doc so callers can sync
      // local state; rejects with { status, message } on failure. The message
      // matters: a 409 explains a license lock (another creator's
      // no-derivatives loop blocks publishing; a forked session stays public).
      return fetch(`${baseUrl}/api/toggle_public`, {
        ...this.getFetchOptions(),
        method: "POST",
        body: JSON.stringify({ session_name: sessionName }),
      }).then(async (response) => {
        const body = await response.json().catch(() => null);
        if (!response.ok) {
          throw { status: response.status, message: body?.message || null };
        }
        return body && body.data && body.data.results;
      });
    },

    setSessionPrivacy(sessionName, privacy) {
      // Three-state privacy (decision 4): "invite" (members and invitees
      // only), "link" (unlisted; anyone holding the link may listen, signed
      // in or not) or "public" (listed). Creator only, enforced server-side.
      // Resolves to the updated session doc; rejects with { status, message }
      // (a 409 explains a license lock, as with toggle_public).
      return fetch(`${baseUrl}/api/session/privacy`, {
        ...this.getFetchOptions(),
        method: "POST",
        body: JSON.stringify({ session_name: sessionName, privacy }),
      }).then(async (response) => {
        const body = await response.json().catch(() => null);
        if (!response.ok) {
          throw { status: response.status, message: body?.message || null };
        }
        return body && body.data && body.data.results;
      });
    },

    fetchMyLicenseSummary(sessionName) {
      // How many of MY loops in this session are rights reserved / CC0, and
      // whether the offer to release them applies (can_release: the session is
      // public and I have rights-reserved loops). Members only: 403 for anyone
      // else. Resolves to the results object; rejects with the HTTP status.
      return fetch(
        `${baseUrl}/api/session/${encodeURIComponent(sessionName)}/my-license-summary`,
        this.getFetchOptions(),
      ).then(async (response) => {
        if (!response.ok) throw response.status;
        const body = await response.json().catch(() => null);
        return (body && body.data && body.data.results) || null;
      });
    },

    relicenseMine(sessionName) {
      // Release MY rights-reserved loops in this session as CC0. Never touches
      // anyone else's loops. Resolves to { changed, count, failed }; rejects
      // with { status, message }.
      return fetch(
        `${baseUrl}/api/session/${encodeURIComponent(sessionName)}/relicense-mine`,
        {
          ...this.getFetchOptions(),
          method: "POST",
          body: JSON.stringify({ license_type: "CC0" }),
        },
      ).then(async (response) => {
        const body = await response.json().catch(() => null);
        if (!response.ok) {
          throw { status: response.status, message: body?.message || null };
        }
        return (body && body.data && body.data.results) || null;
      });
    },

    acceptInvitation(invitationId) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/invitations/${invitationId}/accept`, {
          ...this.getFetchOptions(),
          method: "POST",
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    declineInvitation(invitationId) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/invitations/${invitationId}/decline`, {
          ...this.getFetchOptions(),
          method: "POST",
        })
          .then((response) => {
            if (response.ok) return response.json();
            else reject(response.status);
          })
          .then(resolve)
          .catch(reject);
      });
    },

    fetchInviteByToken(token) {
      return new Promise((resolve, reject) => {
        fetch(`${baseUrl}/api/invite/${token}`)
          .then((response) => {
            response.json().then((body) => {
              if (response.ok) resolve(body);
              else reject({ status: response.status, body });
            });
          })
          .catch(reject);
      });
    },
  },
};
